import axios from 'axios'
import config from '@/assets/js/conf/config'

const ossClient = axios.create({
  withCredentials: false,
})

function authHeaders() {
  if (typeof localStorage === 'undefined') return {}
  const token = localStorage.getItem(config.tokenKey)
  return token ? { Authorization: `Bearer ${token}` } : {}
}

function normalizeMimeType(file) {
  const mimeType = String((file && file.type) || '').toLowerCase()
  if (mimeType === 'image/jpg') return 'image/jpeg'
  if (mimeType === 'video/mpeg4') return 'video/mp4'
  if (mimeType === 'application/x-zip-compressed' || (!mimeType && fileSuffix(file) === '.zip')) return 'application/zip'
  return mimeType
}

function fileSuffix(file) {
  const name = String((file && file.name) || '')
  const match = name.match(/\.([a-z0-9]+)$/i)
  return match ? `.${match[1].toLowerCase()}` : ''
}

function createPolicyMatchedUploadFile(file, mimeType) {
  if (String(file.type || '').toLowerCase() === mimeType) return file

  const name = file.name || 'upload'
  if (typeof File === 'function') {
    return new File([file], name, {
      type: mimeType,
      lastModified: file.lastModified || Date.now(),
    })
  }

  return new Blob([file], {type: mimeType})
}

export async function uploadDirectOss(file, options = {}) {
  if (!file) throw new Error('请选择要上传的文件')
  const purpose = String(options.purpose || '').trim()
  // 专用授权接口通过 policyParams 区分用途，只有通用接口强制要求 purpose。
  if (!options.policyUrl && !purpose) throw new Error('缺少OSS上传用途')

  const policyResponse = await axios.get(
    options.policyUrl || `${config.baseApi}/merchant/shop/ossUploadToken`,
    {
      headers: options.headers || authHeaders(),
      params: {
        ...(options.policyParams || {}),
        ...(purpose ? { purpose } : {}),
        mime_type: normalizeMimeType(file),
        file_name: file.name || 'upload',
      },
    }
  )
  const policyResult = policyResponse.data || {}
  if (Number(policyResult.code) !== 200 || !policyResult.data) {
    throw new Error(policyResult.message || policyResult.msg || '获取OSS上传授权失败')
  }
  const token = policyResult.data
  const mimeType = token.content_type || normalizeMimeType(file)
  const uploadFile = createPolicyMatchedUploadFile(file, mimeType)
  const formData = new FormData()
  formData.append('key', token.key)
  formData.append('policy', token.policy)
  formData.append('OSSAccessKeyId', token.accessid)
  formData.append('Signature', token.signature)
  formData.append('x-oss-security-token', token.security_token)
  formData.append('success_action_status', '200')
  formData.append('Content-Type', mimeType)
  if (token.object_acl) {
    formData.append('x-oss-object-acl', token.object_acl)
  }
  // OSS 会校验 multipart 文件分段的 MIME；不能仅依赖同名表单字段。
  formData.append('file', uploadFile, file.name || 'upload')

  try {
    await ossClient.post(token.host, formData, {
      timeout: options.timeout || 60000,
      onUploadProgress: event => {
        if (event.total && options.onProgress) {
          options.onProgress({ percent: Math.round((event.loaded * 100) / event.total) })
        }
      },
    })
  } catch (error) {
    if (!error.response) {
      throw new Error('OSS直传被浏览器拦截，请检查Bucket CORS及OSS地域节点配置')
    }
    throw error
  }

  const url = token.url || `${String(token.host || '').replace(/\/$/, '')}/${token.key}`
  return {
    url,
    address: url,
    object_name: token.object_name || file.name || '',
    suffix: token.suffix || fileSuffix(file),
    storage_path: token.storage_path || token.key,
  }
}

export function createDirectUploadRequest(options, uploadOptions = {}) {
  uploadDirectOss(options.file, {
    ...uploadOptions,
    onProgress: options.onProgress,
  })
    .then(data => options.onSuccess && options.onSuccess({ code: 200, data }))
    .catch(error => options.onError && options.onError(error))
}
